"use strict";

exports.__esModule = true;
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _reactTable = require("@tanstack/react-table");
var _DataTable = require("./DataTable2.scroll");
var _DataTable2 = require("./DataTable2.tools");
var _useUserData = _interopRequireDefault(require("../../hooks/useUserData"));
var _useParamsOrLocalStorage = _interopRequireDefault(require("../../hooks/useParamsOrLocalStorage"));
var _diacritics = require("../../helpers/diacritics");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
const DataTable = _ref => {
  let {
    id: dataTableId,
    columns: initialColumns,
    data: initialData,
    initialState = {},
    dataTableTools,
    stripped,
    hoverable,
    bordered,
    disableScrollToTop,
    selectable,
    subComponent,
    backEndManagment,
    className,
    children,
    onSelectRows,
    onDataTableToolChange,
    onPageIndexChange,
    stickyRowPosition,
    displayDebug = false
  } = _ref;
  const datatableRef = _react.default.useRef(null);
  const tableRef = _react.default.useRef(null);
  const tableWrapperRef = _react.default.useRef(null);
  const tableTopScrollbarRef = _react.default.useRef(null);
  const tableBottomScrollbarRef = _react.default.useRef(null);
  const {
    handleGrab,
    handleRelease,
    handleMove,
    handleWheel
  } = (0, _DataTable.useScroll)(tableWrapperRef, tableTopScrollbarRef, tableBottomScrollbarRef);
  const [globalFilter, setGlobalFilter] = (0, _useUserData.default)(dataTableId, 'globalFilter', initialState.globalFilter || '');
  const [searchTerm, setSearchTerm] = (0, _useParamsOrLocalStorage.default)(dataTableId, 'searchTerm', initialState.searchTerm || '');
  const [columnFilters, setColumnFilters] = _react.default.useState(initialState.columnFilters || []);
  const [rowSelection, setRowSelection] = _react.default.useState({});
  const [sorting, setSorting] = (0, _useUserData.default)(dataTableId, 'sorting', initialState.sorting || []);
  const [columnOrder, setColumnOrder] = (0, _useUserData.default)(dataTableId, 'columnOrder', initialState.columnOrder || []);
  const [columnSizing, setColumnSizing] = (0, _useUserData.default)(dataTableId, 'columnSizing', initialState.columnSizing || []);
  const [columnVisibility, setColumnVisibility] = (0, _useUserData.default)(dataTableId, 'columnVisibility', initialState.columnVisibility || {});
  const [bkLoading, setBkLoading] = _react.default.useState(false);
  const columns = _react.default.useMemo(() => {
    if (selectable) initialColumns.unshift(_DataTable2.rowSelectionColumnDef);
    let rtColumns = Object.assign([], initialColumns);
    rtColumns = rtColumns.map(col => {
      const newCol = {
        ...col,
        footer: col.sticky
      };
      delete newCol.sticky;
      return newCol;
    });
    return rtColumns;
  }, [initialColumns, selectable]);
  const data = _react.default.useMemo(() => {
    setBkLoading(false);
    setRowSelection({});
    return initialData;
  }, [initialData]);
  const tools = _react.default.useMemo(() => {
    return {
      // selectable: selectable,
      filterInput: {
        ...DataTable.defaultProps.dataTableTools.filterInput,
        ...dataTableTools.filterInput
      },
      count: {
        ...DataTable.defaultProps.dataTableTools.count,
        ...dataTableTools.count
      },
      filterToggle: {
        ...DataTable.defaultProps.dataTableTools.filterToggle,
        ...dataTableTools.filterToggle
      },
      pages: {
        ...DataTable.defaultProps.dataTableTools.pages,
        ...dataTableTools.pages
      },
      pagination: {
        ...DataTable.defaultProps.dataTableTools.pagination,
        ...dataTableTools.pagination
      },
      pager: {
        ...DataTable.defaultProps.dataTableTools.pager,
        ...dataTableTools.pager
      },
      columnsToggle: {
        ...DataTable.defaultProps.dataTableTools.columnsToggle,
        ...dataTableTools.columnsToggle
      },
      selectable: {
        ...DataTable.defaultProps.dataTableTools.selectable,
        ...dataTableTools.selectable,
        fn: selectable
      },
      custom: dataTableTools.custom
    };
  }, [dataTableTools, selectable]);
  const [{
    pageIndex,
    pageSize
  }, setPagination] = (0, _useUserData.default)(dataTableId, 'pagination', initialState.pagination || {
    pageIndex: 0,
    pageSize: 10
  });
  const pagination = _react.default.useMemo(() => {
    const maxPageSize = Array.isArray(tools.pages.options) ? Math.max(...tools.pages.options) || 10 : 10;

    // ensure the requested 'pageSize' exists in the available options
    // otherwise fallback on maximum available option
    return {
      pageIndex,
      pageSize: tools.pages.options.includes(pageSize) ? pageSize : maxPageSize
    };
  }, [pageIndex, pageSize, tools.pages.options]);
  const {
    fetchData,
    setBackEndFilter,
    pageCount: controlledPageCount,
    entriesCount: controlledEntriesCount,
    loading
  } = backEndManagment;
  const managedByBackend = (backEndManagment == null ? void 0 : backEndManagment.fetchData) && typeof backEndManagment.fetchData === 'function';
  const table = (0, _reactTable.useReactTable)({
    data,
    columns,
    defaultColumn: {
      size: 160,
      minSize: 80,
      maxSize: Number.MAX_SAFE_INTEGER
    },
    initialState: {
      ...initialState
    },
    enableFilters: true,
    filterFns: {
      boolTrue: (row, id, filterValue) => {
        const rowValue = row.original[id];
        if (rowValue === undefined) {
          return true;
        }
        return filterValue !== rowValue;
      },
      boolFalse: (row, id, filterValue) => {
        const rowValue = row.original[id];
        if (rowValue === undefined) {
          return false;
        }
        return filterValue === rowValue;
      }
    },
    onColumnFiltersChange: setColumnFilters,
    globalFilterFn: (row, columnId, filterValue) => {
      const safeValue = (() => {
        const value = row.getValue(columnId);
        return typeof value === 'number' ? String(value) : value;
      })();
      return (0, _diacritics.stripDiacritics)(safeValue == null ? void 0 : safeValue.toLowerCase()).includes((0, _diacritics.stripDiacritics)(filterValue.toLowerCase()));
    },
    onGlobalFilterChange: setGlobalFilter,
    enableRowSelection: true,
    enableMultiRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnOrderChange: setColumnOrder,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    onColumnSizingChange: setColumnSizing,
    enableHiding: true,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    enableExpanding: true,
    manualFiltering: managedByBackend,
    manualPagination: managedByBackend,
    manualSorting: managedByBackend,
    manualExpanding: managedByBackend,
    state: {
      globalFilter,
      columnFilters,
      rowSelection,
      sorting,
      columnOrder: selectable ? ['select', ...columnOrder] : columnOrder,
      columnSizing,
      columnVisibility,
      pagination
    },
    getCoreRowModel: (0, _reactTable.getCoreRowModel)(),
    getSortedRowModel: (0, _reactTable.getSortedRowModel)(),
    getFilteredRowModel: (0, _reactTable.getFilteredRowModel)(),
    getPaginationRowModel: (0, _reactTable.getPaginationRowModel)(),
    getExpandedRowModel: (0, _reactTable.getExpandedRowModel)(),
    debugTable: displayDebug,
    debugHeaders: displayDebug,
    debugColumns: displayDebug
  });
  const renderRowSubComponent = _react.default.useCallback(_ref2 => {
    let {
      row
    } = _ref2;
    return subComponent && typeof subComponent === 'function' && subComponent(row);
  }, [subComponent]);
  const isFlexible = table.options.columns.filter(col => {
    var _col$meta;
    return col == null || (_col$meta = col.meta) == null ? void 0 : _col$meta.flexibleCol;
  }).length > 0;

  // Built an Object to pass the states to each DataTableTool
  const dataTableToolsParams = {
    ...table,
    tools,
    managedByBackend,
    // For the Backend tools
    loading,
    bkLoading,
    searchTerm,
    setSearchTerm,
    bkPageCount: Number(controlledPageCount),
    bkEntriesCount: Number(controlledEntriesCount),
    setBackEndFilter
  };

  // Listen for changes in pages, sorting and/or filters to reset pagination and use onDataTableToolChange callback
  _react.default.useEffect(() => {
    table.resetPageIndex();
    if (typeof onDataTableToolChange === 'function') {
      onDataTableToolChange({
        pageSize: pagination.pageSize,
        sortBy: sorting,
        backendSearchTerm: searchTerm || '',
        frontendSearchTerm: globalFilter || ''
      });
    }
  }, [table, fetchData, onDataTableToolChange, pagination.pageSize, sorting, globalFilter, searchTerm
  // controlledEntriesCount
  ]);

  // Close all expanded subComponents when column order/sorting or visibility change
  _react.default.useEffect(() => {
    table.resetExpanded();
  }, [table, columnOrder, columnVisibility, sorting]);

  // Listen for the change in rowSelection to use onSelectRows callback
  _react.default.useEffect(() => {
    if (typeof onSelectRows === 'function') {
      const rowSelected = Object.keys(rowSelection).map(key => {
        var _table$getFilteredRow;
        return (_table$getFilteredRow = table.getFilteredRowModel()) == null || (_table$getFilteredRow = _table$getFilteredRow.rowsById[key]) == null ? void 0 : _table$getFilteredRow.original;
      });
      onSelectRows(rowSelected);
    }
  }, [onSelectRows, rowSelection, table]);

  // Listen for changes in pagination, sorting and/or filters to reset rowSelection/rowExpanded and fetch the new data (if backend)
  _react.default.useEffect(() => {
    if (fetchData) {
      table.resetRowSelection();
      table.resetExpanded();
      setBkLoading(true);
      fetchData({
        pageIndex,
        pageSize: pagination.pageSize,
        request: searchTerm || '',
        sortBy: sorting
      });
    }
  }, [table, fetchData, pageIndex, pagination.pageSize, sorting, searchTerm, dataTableId]);
  _react.default.useEffect(() => {
    if (pageIndex && !disableScrollToTop) {
      const offset = datatableRef.current.offsetTop - document.querySelector('.uk-main__heading').offsetHeight;
      document.querySelector('main').scrollTo({
        top: offset,
        behavior: 'smooth'
      });
    }
    if (typeof onPageIndexChange === 'function') {
      onPageIndexChange({
        pageIndex
      });
    }
  }, [pageIndex, disableScrollToTop, onPageIndexChange]);
  return /*#__PURE__*/_react.default.createElement("section", {
    className: (0, _clsx.default)('uk-datatable', className),
    ref: datatableRef
  }, displayDebug && /*#__PURE__*/_react.default.createElement("pre", null, JSON.stringify(table.getState(), null, 2)), children, dataTableTools && /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-datatable__tools uk-datatable__tools--top"
  }, /*#__PURE__*/_react.default.createElement(_DataTable2.DataTableTools, {
    position: "top-left",
    instance: dataTableToolsParams
  }), /*#__PURE__*/_react.default.createElement(_DataTable2.DataTableTools, {
    position: "top-center",
    instance: dataTableToolsParams
  }), /*#__PURE__*/_react.default.createElement(_DataTable2.DataTableTools, {
    position: "top-right",
    instance: dataTableToolsParams
  })), /*#__PURE__*/_react.default.createElement("div", {
    ref: tableTopScrollbarRef,
    className: "uk-datatable__scrollbar"
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-datatable__scrollbar-thumb",
    style: {
      width: tableWrapperRef.current ? tableWrapperRef.current.scrollWidth + 'px' : 0
    }
  })), /*#__PURE__*/_react.default.createElement("div", {
    ref: tableWrapperRef,
    className: "uk-datatable__table"
  }, /*#__PURE__*/_react.default.createElement("table", {
    ref: tableRef,
    id: dataTableId,
    className: (0, _clsx.default)('uk-table', isFlexible && 'uk-table--wide', stripped && 'uk-table--stripped', hoverable && 'uk-table--hoverable', bordered && 'uk-table--bordered'),
    onMouseDown: e => handleGrab(e, tableWrapperRef),
    onMouseUp: e => handleRelease(e, tableWrapperRef),
    onMouseLeave: e => handleRelease(e, tableWrapperRef),
    onMouseMove: e => handleMove(e, tableWrapperRef),
    onWheel: e => handleWheel(e, tableWrapperRef)
  }, /*#__PURE__*/_react.default.createElement("thead", {
    className: "uk-datatable__thead"
  }, table.getHeaderGroups().map(headerGroup => /*#__PURE__*/_react.default.createElement("tr", {
    key: headerGroup.id,
    className: "uk-datatable__tr"
  }, headerGroup.headers.map((header, headerIdx) => {
    var _header$column$column, _header$column$column2, _asc$desc$header$colu;
    return /*#__PURE__*/_react.default.createElement("th", {
      key: header.id,
      colSpan: header.colSpan,
      style: (_header$column$column = header.column.columnDef) != null && (_header$column$column = _header$column$column.meta) != null && _header$column$column.flexibleCol ? {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: header.getSize()
      } : {
        width: header.getSize()
      },
      className: (0, _clsx.default)('uk-datatable__th', (_header$column$column2 = header.column.columnDef) == null || (_header$column$column2 = _header$column$column2.meta) == null ? void 0 : _header$column$column2.headerClassName, header.column.getCanResize() && 'position-relative', data.length > 1 && [header.column.getCanSort() && 'uk-datatable__th--sortable', header.column.getIsSorted() && "uk-datatable__th--sorted-" + header.column.getIsSorted()]),
      onMouseUp: header.column.getIsResizing() ? null : header.column.getToggleSortingHandler()
    }, header.isPlaceholder ? null : /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, (_asc$desc$header$colu = {
      asc: /*#__PURE__*/_react.default.createElement("div", {
        className: "uk-svg uk-data-table__sorter"
      }, /*#__PURE__*/_react.default.createElement("svg", {
        xmlns: "http://www.w3.org/2000/svg",
        viewBox: "0 0 320 512"
      }, /*#__PURE__*/_react.default.createElement("path", {
        d: "M279 224H41c-21.4 0-32.1-25.9-17-41L143 64c9.4-9.4 24.6-9.4 33.9 0l119 119c15.2 15.1 4.5 41-16.9 41z"
      }))),
      desc: /*#__PURE__*/_react.default.createElement("div", {
        className: "uk-svg uk-data-table__sorter"
      }, /*#__PURE__*/_react.default.createElement("svg", {
        xmlns: "http://www.w3.org/2000/svg",
        viewBox: "0 0 320 512"
      }, /*#__PURE__*/_react.default.createElement("path", {
        d: "M41 288h238c21.4 0 32.1 25.9 17 41L177 448c-9.4 9.4-24.6 9.4-33.9 0L24 329c-15.1-15.1-4.4-41 17-41z"
      })))
    }[header.column.getIsSorted()]) != null ? _asc$desc$header$colu : null, header.column.getCanSort() && !header.column.getIsSorted() ? /*#__PURE__*/_react.default.createElement("div", {
      className: "uk-svg uk-data-table__sorter"
    }, /*#__PURE__*/_react.default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 320 512"
    }, /*#__PURE__*/_react.default.createElement("path", {
      d: "M41 288h238c21.4 0 32.1 25.9 17 41L177 448c-9.4 9.4-24.6 9.4-33.9 0L24 329c-15.1-15.1-4.4-41 17-41zm255-105L177 64c-9.4-9.4-24.6-9.4-33.9 0L24 183c-15.1 15.1-4.4 41 17 41h238c21.4 0 32.1-25.9 17-41z"
    }))) : null, /*#__PURE__*/_react.default.createElement("span", null, (0, _reactTable.flexRender)(header.column.columnDef.header, header.getContext()))), header.column.getCanResize() && /*#__PURE__*/_react.default.createElement("div", {
      onMouseDown: header.getResizeHandler(),
      onTouchStart: header.getResizeHandler(),
      onClick: event => event.stopPropagation(),
      className: (0, _clsx.default)('uk-datatable__resizer', header.column.getIsResizing() && 'uk-datatable__resizer--isResizing')
    }));
  })))), /*#__PURE__*/_react.default.createElement("tbody", {
    className: "uk-datatable__tbody " + (loading || bkLoading ? 'uk-datatable--isLoading' : '')
  }, stickyRowPosition !== 'bottom' && table.getFooterGroups()[0].headers.filter(f => f.column.columnDef.footer).length > 0 // Is there a sticky/footer definition in a column?
  && Array.from(managedByBackend ? [...table.getPreSortedRowModel().rows] : [...table.getRowModel().rows]).length > 0 // Is there any row ?
  && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, table.getFooterGroups().map(headerGroup => /*#__PURE__*/_react.default.createElement("tr", {
    key: headerGroup.id,
    className: "uk-datatable__tr uk-datatable__tr--sticky"
  }, headerGroup.headers.map((header, headerIdx) => {
    var _header$column$column3, _header$column$column4;
    return /*#__PURE__*/_react.default.createElement("td", {
      key: header.id,
      colSpan: header.colSpan,
      style: (_header$column$column3 = header.column.columnDef) != null && (_header$column$column3 = _header$column$column3.meta) != null && _header$column$column3.flexibleCol ? {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: header.getSize()
      } : {
        width: header.getSize()
      },
      className: (0, _clsx.default)('uk-datatable__td', (_header$column$column4 = header.column.columnDef) == null || (_header$column$column4 = _header$column$column4.meta) == null ? void 0 : _header$column$column4.stickyRowClassName)
    }, /*#__PURE__*/_react.default.createElement("span", null, (0, _reactTable.flexRender)(header.column.columnDef.footer, header.getContext())));
  })))), (loading || bkLoading) && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement("tr", null), /*#__PURE__*/_react.default.createElement("tr", {
    className: "uk-datatable__tr--isLoading"
  }, /*#__PURE__*/_react.default.createElement("td", {
    colSpan: table.getVisibleFlatColumns().length
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-loading__wrapper"
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "loading"
  }, /*#__PURE__*/_react.default.createElement("div", null)))))), Array.from(managedByBackend ? [...table.getPreSortedRowModel().rows] : [...table.getRowModel().rows]).map((row, i) => {
    return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, {
      key: row.id
    }, /*#__PURE__*/_react.default.createElement("tr", {
      className: (0, _clsx.default)('uk-datatable__tr', row.getIsSelected() && 'uk-datatable__tr--selected', row.getIsExpanded() && 'uk-datatable__tr--expanded')
    }, row.getVisibleCells().map((cell, cellIdx) => {
      var _cell$column$columnDe, _cell$column$columnDe2, _cell$column$columnDe3;
      return /*#__PURE__*/_react.default.createElement("td", {
        key: cell.id,
        style: (_cell$column$columnDe = cell.column.columnDef) != null && (_cell$column$columnDe = _cell$column$columnDe.meta) != null && _cell$column$columnDe.flexibleCol ? {
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: cell.column.getSize()
        } : {
          width: cell.column.getSize()
        },
        className: (0, _clsx.default)('uk-datatable__td', (_cell$column$columnDe2 = cell.column.columnDef) == null || (_cell$column$columnDe2 = _cell$column$columnDe2.meta) == null ? void 0 : _cell$column$columnDe2.className // cell.column.columnDef?.id === 'actions' && 'uk-datatable__actions'
        )
      }, /*#__PURE__*/_react.default.createElement("div", {
        className: (_cell$column$columnDe3 = cell.column.columnDef) != null && (_cell$column$columnDe3 = _cell$column$columnDe3.meta) != null && _cell$column$columnDe3.needOverflow ? '' : 'uk-datatable__td-inner'
      }, (0, _reactTable.flexRender)(cell.column.columnDef.cell, cell.getContext())));
    })), row.getIsExpanded() && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement("tr", null), /*#__PURE__*/_react.default.createElement("tr", {
      key: "row_expanded_" + i,
      className: (0, _clsx.default)('uk-datatable__tr', 'is-expanded', 'uk-datatable__tr--subRow'
      // row.values.archived && 'is-archived'
      )
    }, /*#__PURE__*/_react.default.createElement("td", {
      colSpan: row.getVisibleCells().length,
      style: {
        minWidth: table.getTotalSize(),
        width: '100%'
      }
    }, renderRowSubComponent({
      row
    })))));
  }), stickyRowPosition !== 'top' && table.getFooterGroups()[0].headers.filter(f => f.column.columnDef.footer).length > 0 // Is there a sticky/footer definition in a column?
  && Array.from(managedByBackend ? [...table.getPreSortedRowModel().rows] : [...table.getRowModel().rows]).length > 0 // Is there any row ?
  && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, table.getFooterGroups().map(headerGroup => /*#__PURE__*/_react.default.createElement("tr", {
    key: headerGroup.id,
    className: "uk-datatable__tr uk-datatable__tr--sticky"
  }, headerGroup.headers.map((header, headerIdx) => {
    var _header$column$column5, _header$column$column6;
    return /*#__PURE__*/_react.default.createElement("td", {
      key: header.id,
      colSpan: header.colSpan,
      style: (_header$column$column5 = header.column.columnDef) != null && (_header$column$column5 = _header$column$column5.meta) != null && _header$column$column5.flexibleCol ? {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: header.getSize()
      } : {
        width: header.getSize()
      },
      className: (0, _clsx.default)('uk-datatable__td', (_header$column$column6 = header.column.columnDef) == null || (_header$column$column6 = _header$column$column6.meta) == null ? void 0 : _header$column$column6.stickyRowClassName)
    }, /*#__PURE__*/_react.default.createElement("span", null, (0, _reactTable.flexRender)(header.column.columnDef.footer, header.getContext())));
  }))))))), /*#__PURE__*/_react.default.createElement("div", {
    ref: tableBottomScrollbarRef,
    className: "uk-datatable__scrollbar"
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-datatable__scrollbar-thumb",
    style: {
      width: tableWrapperRef.current ? tableWrapperRef.current.scrollWidth + 'px' : 0
    }
  })), dataTableTools && /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-datatable__tools uk-datatable__tools--bottom"
  }, /*#__PURE__*/_react.default.createElement(_DataTable2.DataTableTools, {
    position: "bottom-left",
    instance: dataTableToolsParams
  }), /*#__PURE__*/_react.default.createElement(_DataTable2.DataTableTools, {
    position: "bottom-center",
    instance: dataTableToolsParams
  }), /*#__PURE__*/_react.default.createElement(_DataTable2.DataTableTools, {
    position: "bottom-right",
    instance: dataTableToolsParams
  })));
};
DataTable.propTypes = {
  /**
   * ID of the Switch (used to bind the label to the Switch)
   * NB: the ID will be randomly generated if not set.
   */
  id: _propTypes.default.string.isRequired,
  /**
   * ID of the Switch (used to bind the label to the Switch)
   * NB: the ID will be randomly generated if not set.
   */
  columns: _propTypes.default.any,
  /**
   * ID of the Switch (used to bind the label to the Switch)
   * NB: the ID will be randomly generated if not set.
   */
  data: _propTypes.default.any,
  /**
   * ID of the Switch (used to bind the label to the Switch)
   * NB: the ID will be randomly generated if not set.
   */
  initialState: _propTypes.default.any,
  /**
   * ID of the Switch (used to bind the label to the Switch)
   * NB: the ID will be randomly generated if not set.
   */
  dataTableTools: _propTypes.default.any,
  /**
   * ID of the Switch (used to bind the label to the Switch)
   * NB: the ID will be randomly generated if not set.
   */
  stripped: _propTypes.default.bool,
  /**
   * ID of the Switch (used to bind the label to the Switch)
   * NB: the ID will be randomly generated if not set.
   */
  hoverable: _propTypes.default.bool,
  /**
   * ID of the Switch (used to bind the label to the Switch)
   * NB: the ID will be randomly generated if not set.
   */
  bordered: _propTypes.default.bool,
  /**
   * Write 'top' to switch the scrollbar position
   * NB: 'bottom' default position
   */
  stickyRowPosition: _propTypes.default.oneOf(['top', 'bottom', 'both']),
  /**
   * ID of the Switch (used to bind the label to the Switch)
   * NB: the ID will be randomly generated if not set.
   */
  subComponent: _propTypes.default.any
};
DataTable.defaultProps = {
  initialState: {},
  stripped: false,
  hoverable: false,
  bordered: false,
  stickyRowPosition: 'bottom',
  selectable: false,
  disableScrollToTop: false,
  dataTableTools: {
    filterInput: {
      position: 'top-left',
      placeholder: 'Filter by ...'
    },
    count: {
      position: 'top-left',
      entry: 'entry',
      entries: 'entries'
    },
    filterToggle: {
      position: 'top-center',
      key: 'archived',
      label: 'Show archived entries'
    },
    pages: {
      position: 'top-right',
      label: '',
      allOptionsLabel: '',
      options: [5, 10, 20, 50, 100],
      labels: {
        before: '',
        after: ''
      }
    },
    pagination: {
      position: 'bottom-left'
    },
    pager: {
      position: 'bottom-right'
    },
    custom: []
  },
  backEndManagment: {
    fetchData: null,
    loading: false,
    pageCount: 0,
    entriesCount: 0
  }
};
var _default = exports.default = DataTable;